import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { BookOpen, Boxes, Menu, Moon, Sun, TriangleAlert, X } from "lucide-react";

import { pedir } from "../api/cliente.ts";
import { usarTema } from "../ganchos/tema.ts";
import { MarcaCompleta } from "../componentes/Marca.tsx";
import { Aviso, Cargando, cx } from "../componentes/ui.tsx";
import { ErrorConsulta } from "../componentes/Errores.tsx";
import {
  agrupar,
  anclaDe,
  type Contrato,
  type Guia,
  type Sdk,
  type Operacion,
} from "./integracion-modelo.ts";

/**
 * La documentación pública de la API.
 *
 * **No hay una sola frase escrita en este archivo sobre lo que hace la API.**
 * Todo —resúmenes, descripciones, ejemplos, familias, tablas— sale de
 * `GET /api/v1/openapi.json`, que el backend genera desde su propia tabla de
 * rutas y que tiene una prueba exigiendo que ninguna ruta quede sin describir.
 *
 * Es deliberado, y es la única razón por la que esta página puede envejecer sin
 * mentir: si se escribiera aquí, sería una segunda copia de la misma verdad, y
 * el integrador creería esta, porque es la que lee primero.
 *
 * Pública, fuera de la sesión: quien va a integrar tiene que poder leerla antes
 * de tener cuenta.
 */
export function Integracion() {
  const { tema, alternar } = usarTema();
  const [abierto, setAbierto] = useState(false);

  const { data, isPending, error, refetch } = useQuery({
    queryKey: ["contrato"],
    queryFn: ({ signal }) =>
      pedir<Contrato>("/api/v1/openapi.json", { senal: signal, publica: true }),
    staleTime: 5 * 60_000,
    retry: false,
  });

  const familias = useMemo(() => (data ? agrupar(data) : []), [data]);

  // Cerrar el cajón al saltar a una sección: en móvil tapa justo lo que se pidió.
  useEffect(() => {
    if (!abierto) return;
    const cerrar = () => setAbierto(false);
    window.addEventListener("hashchange", cerrar);
    return () => window.removeEventListener("hashchange", cerrar);
  }, [abierto]);

  if (isPending) return <Cargando texto="Cargando el contrato…" />;
  if (error) return <ErrorConsulta error={error} alReintentar={() => void refetch()} />;

  const guia = data["x-guia"];
  // Puede no venir: un servidor anterior a SPEC-027 no lo publica, y la página
  // tiene que seguir sirviendo — es pública y la lee gente que no controla qué
  // versión tiene delante.
  const sdk = data["x-sdk"];
  // Sin recortar la barra, un servidor declarado como "/" —el defecto en
  // desarrollo— pinta "//api/v1", que parece un error de la API y no lo es.
  const base = (data.servers?.[0]?.url ?? "").replace(/\/+$/, "");

  const indice = (
    <nav className="flex flex-col gap-5 p-5 text-sm" aria-label="Contenido">
      <div className="flex items-center justify-between">
        <MarcaCompleta />
        <button
          onClick={() => setAbierto(false)}
          className="rounded-lg p-1.5 text-tenue hover:bg-superficie-2 lg:hidden"
          aria-label="Cerrar el índice"
        >
          <X className="size-5" aria-hidden />
        </button>
      </div>

      {/* El SDK va PRIMERO y como tarjeta, no como un renglón más.
          Al mismo peso que "Endpoint" se perdía: quien recorre la lista con la
          vista lee cinco entradas parecidas y sigue de largo — y justamente
          esta es la que le ahorra escribir todo lo demás.
          Va antes que "Qué es esto" a propósito: el h1 ya dice que esto es la
          API de 4drando, así que lo primero accionable no es la explicación,
          es que existe una librería que la implementa. El orden de la página es
          el mismo, para que el índice no obligue a buscar dos veces. */}
      {sdk && (
        <a
          href="#sdk"
          className="flex items-start gap-2.5 rounded-lg border border-marca/30 bg-marca-suave/50 px-3 py-2.5 transition-colors hover:border-marca/50"
        >
          <Boxes className="mt-0.5 size-4 shrink-0 text-marca" aria-hidden />
          <span className="flex flex-col">
            <span className="font-medium text-tinta">{sdk.titulo}</span>
            {sdk.subtitulo && <span className="text-xs text-tenue">{sdk.subtitulo}</span>}
          </span>
        </a>
      )}

      {/* Este encabezado nació de subir el SDK. Con la tarjeta arriba y estas
          cuatro entradas sueltas debajo, parecían colgar de ella: no había
          forma de saber dónde termina el SDK y dónde empieza la API. Las
          familias de rutas ya tenían el suyo; este bloque era el único sin
          etiqueta, y se notaba justo desde que dejó de ir primero. */}
      <div className="flex flex-col gap-1">
        <TituloGrupo>La API</TituloGrupo>
        {guia.apertura && <Salto a="#que-es">{guia.apertura.titulo}</Salto>}
        <Salto a="#autenticacion">Autenticación</Salto>
        <Salto a="#endpoint">Endpoint</Salto>
        <Salto a="#cuenta-de">Para tener en cuenta</Salto>
      </div>

      {familias.map((f) => (
        <div key={f.id} className="flex flex-col gap-1">
          <TituloGrupo>{f.titulo}</TituloGrupo>
          {f.operaciones.map((op) => (
            <Salto key={anclaDe(op)} a={`#${anclaDe(op)}`}>
              <Metodo metodo={op.metodo} />
              <span className="truncate">{op.resumen}</span>
            </Salto>
          ))}
        </div>
      ))}

      <div className="flex flex-col gap-1">
        <Salto a="#estados">{guia.estados.titulo}</Salto>
        <Salto a="#codigos">{guia.errores.titulo}</Salto>
      </div>
    </nav>
  );

  // 96rem, el mismo tope que el Marco del panel: ensanchar sin límite obliga a
  // recorrer con la vista de un extremo a otro para no perder el renglón.
  return (
    <div className="mx-auto min-h-dvh max-w-[96rem] bg-fondo lg:grid lg:grid-cols-[19rem_1fr]">
      <aside className="sticky top-0 hidden h-dvh overflow-y-auto border-r border-borde bg-superficie lg:block">
        {indice}
      </aside>

      {abierto && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <button
            className="absolute inset-0 bg-black/45 backdrop-blur-[2px]"
            onClick={() => setAbierto(false)}
            aria-label="Cerrar el índice"
          />
          <aside className="absolute inset-y-0 left-0 w-[19rem] overflow-y-auto border-r border-borde bg-superficie shadow-flotante subir">
            {indice}
          </aside>
        </div>
      )}

      <div className="min-w-0">
        <header className="sticky top-0 z-30 flex items-center gap-3 border-b border-borde bg-superficie/85 px-4 py-3 backdrop-blur-md lg:justify-end">
          <button
            onClick={() => setAbierto(true)}
            className="rounded-lg p-1.5 text-tenue hover:bg-superficie-2 hover:text-tinta lg:hidden"
            aria-label="Abrir el índice"
          >
            <Menu className="size-5" aria-hidden />
          </button>
          <MarcaCompleta className="flex-1 lg:hidden" />
          <button
            onClick={alternar}
            className="rounded-lg p-1.5 text-tenue hover:bg-superficie-2 hover:text-tinta"
            aria-label={tema === "oscuro" ? "Tema claro" : "Tema oscuro"}
          >
            {tema === "oscuro" ? <Sun className="size-5" aria-hidden /> : <Moon className="size-5" aria-hidden />}
          </button>
        </header>

        {/* Sin tope propio: los párrafos ya se acotan a 64ch donde hace falta, y
            lo que quiere ancho —código, tablas de respuestas— lo aprovecha. */}
        <main className="w-full px-5 pt-8 pb-24 sm:px-8">
          <p className="flex items-center gap-2 text-xs font-semibold tracking-wider text-marca uppercase">
            <BookOpen className="size-4" aria-hidden />
            Documentación
          </p>
          <h1 className="mt-2 text-3xl font-extrabold tracking-tight text-balance text-tinta sm:text-4xl">
            API de {data.info.title}
          </h1>

          {sdk && <ElCliente sdk={sdk} />}

          {guia.apertura && <QueEsEsto apertura={guia.apertura} />}

          <Seccion id="autenticacion" titulo="Autenticación">
            <ol className="flex flex-col gap-4">
              {guia.autenticacion.map((paso, i) => (
                <li key={paso.titulo} className="flex flex-col gap-2">
                  <p className="font-medium text-tinta">
                    {i + 1}. {paso.titulo}
                  </p>
                  <div className="border-l-2 border-borde-fuerte pl-4">
                    <Prosa>{paso.cuerpo}</Prosa>
                    {paso.codigo && <Codigo className="mt-3">{paso.codigo}</Codigo>}
                  </div>
                </li>
              ))}
            </ol>
          </Seccion>

          <Seccion id="endpoint" titulo="Endpoint">
            <p className="text-tenue">Todas las solicitudes van dirigidas a esta URL base.</p>
            <div className="w-fit border-b-2 border-marca pb-1">
              <p className="text-[11px] font-semibold tracking-wider text-tenue-2 uppercase">URI base</p>
              <p className="cifra mt-1 text-sm">{base}/api/v1</p>
            </div>
          </Seccion>

          <Seccion id="cuenta-de" titulo="Para tener en cuenta">
            <ul className="flex list-disc flex-col gap-2.5 pl-5">
              {guia.tenerEnCuenta.map((t) => (
                <li key={t}>
                  <Prosa>{t}</Prosa>
                </li>
              ))}
            </ul>
          </Seccion>

          {familias.map((f) => (
            <section key={f.id} className="pt-12">
              <h2 className="text-2xl font-extrabold tracking-tight text-tinta">{f.titulo}</h2>
              <p className="mt-2 max-w-[64ch] text-tenue">{f.entrada}</p>
              <div className="mt-6 flex flex-col gap-8">
                {f.operaciones.map((op) => (
                  <Recurso key={anclaDe(op)} op={op} base={base} />
                ))}
              </div>
            </section>
          ))}

          <Seccion id="estados" titulo={guia.estados.titulo}>
            <p className="text-tenue">{guia.estados.entrada}</p>
            <Tabla cabeceras={["Estado", "Qué significa"]} filas={guia.estados.filas} />
          </Seccion>

          <Seccion id="codigos" titulo={guia.errores.titulo}>
            <Tabla cabeceras={["Código", "Cuándo"]} filas={guia.errores.filas} />
          </Seccion>

          <footer className="mt-16 border-t border-borde pt-6 text-sm text-tenue-2">
            <p>
              Esta página se genera desde <code className="cifra">GET /api/v1/openapi.json</code>, que
              a su vez sale de la tabla de rutas del servidor. Si una ruta cambia, esto cambia con
              ella.
            </p>
          </footer>
        </main>
      </div>
    </div>
  );
}

/**
 * Qué es esto, antes de cualquier ruta.
 *
 * La página empezaba en «Autenticación», que es el paso dos: quien llega no sabe
 * todavía qué hace la integración ni qué tiene que aguantar su sistema. El
 * diagrama vive aquí y no en el contrato porque es presentación; los textos
 * vienen del backend, que es quien sabe si `PAGO_REVERSADO` sigue existiendo.
 */
function QueEsEsto({ apertura }: { apertura: NonNullable<Guia["apertura"]> }) {
  return (
    <Seccion id="que-es" titulo={apertura.titulo}>
      <p className="max-w-[64ch] text-tenue">{apertura.entrada}</p>

      <Diagrama />

      <div className="grid gap-3 sm:grid-cols-2">
        {apertura.direcciones.map((d, i) => (
          <div key={d.nombre} className="rounded-xl border border-borde bg-superficie p-4">
            <p
              className={cx(
                "text-[11px] font-semibold tracking-wider uppercase",
                i === 0 ? "text-dato-tinta" : "text-marca-tinta",
              )}
            >
              {d.nombre} — {d.que}
            </p>
            <p className="mt-1.5 text-sm text-tenue">{d.detalle}</p>
            <p className="cifra mt-2 text-xs text-tenue-2">{d.rutas}</p>
          </div>
        ))}
      </div>

      {/* La advertencia va en tono de riesgo y no de error: no es que algo esté
          mal, es que hay una decisión que tomar antes de escribir código. */}
      <Aviso tono="riesgo" titulo={apertura.advertencia.titulo} icono={TriangleAlert}>
        <div className="space-y-2">
          {apertura.advertencia.cuerpo.split("\n\n").map((parrafo) => (
            <p key={parrafo}>
              <Prosa>{parrafo}</Prosa>
            </p>
          ))}
        </div>
      </Aviso>
    </Seccion>
  );
}

/**
 * Las dos direcciones, dibujadas.
 *
 * Colores del sistema: el azul es el lado de quien manda datos y el teal el
 * nuestro, igual que en el resto del panel. Se declara `fill` en cada forma
 * porque un SVG sin fill explícito hereda lo que haya y se pierde en un tema.
 */
function Diagrama() {
  return (
    <figure className="m-0 overflow-x-auto rounded-xl border border-borde bg-superficie p-4">
      <svg
        viewBox="0 0 720 210"
        className="block h-auto w-full min-w-[560px]"
        role="img"
        aria-label="Tu sistema carga facturas y abonos en 4drando; 4drando devuelve los pagos ya resueltos, que tu sistema consulta cuando quiere."
      >
        <defs>
          <marker id="pEnt" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
            <path d="M0,0 L10,5 L0,10 z" fill="var(--color-dato)" />
          </marker>
          <marker id="pSal" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
            <path d="M0,0 L10,5 L0,10 z" fill="var(--color-marca)" />
          </marker>
        </defs>

        <rect x="4" y="52" width="168" height="102" rx="12" fill="var(--color-superficie-2)" stroke="var(--color-borde-fuerte)" />
        <text x="88" y="90" textAnchor="middle" fontSize="16" fontWeight="700" fill="var(--color-tinta)">Tu sistema</text>
        <text x="88" y="112" textAnchor="middle" fontSize="12" fill="var(--color-tenue)">Facturas, abonos,</text>
        <text x="88" y="129" textAnchor="middle" fontSize="12" fill="var(--color-tenue)">cartera</text>

        <rect x="548" y="52" width="168" height="102" rx="12" fill="var(--color-marca-suave)" stroke="var(--color-marca)" />
        <text x="632" y="90" textAnchor="middle" fontSize="16" fontWeight="700" fill="var(--color-marca-tinta)">4drando</text>
        <text x="632" y="112" textAnchor="middle" fontSize="12" fill="var(--color-tenue)">Lee el banco</text>
        <text x="632" y="129" textAnchor="middle" fontSize="12" fill="var(--color-tenue)">y lo cruza</text>

        <line x1="182" y1="82" x2="534" y2="82" stroke="var(--color-dato)" strokeWidth="2" markerEnd="url(#pEnt)" />
        <text x="358" y="42" textAnchor="middle" fontSize="13" fontWeight="600" fill="var(--color-dato)">Entrada — lo que se debe</text>
        <text x="358" y="72" textAnchor="middle" fontSize="11" fill="var(--color-tenue)">POST /abonos/lote · POST /facturas/lote</text>

        <line x1="534" y1="130" x2="182" y2="130" stroke="var(--color-marca)" strokeWidth="2" markerEnd="url(#pSal)" />
        <text x="358" y="152" textAnchor="middle" fontSize="11" fill="var(--color-tenue)">GET /eventos?desde=…</text>
        <text x="358" y="178" textAnchor="middle" fontSize="13" fontWeight="600" fill="var(--color-marca)">Salida — lo que se cobró</text>
        <text x="358" y="198" textAnchor="middle" fontSize="11" fill="var(--color-tenue-2)">Tu sistema consulta cuando quiere. Nosotros no llamamos a nadie.</text>
      </svg>
    </figure>
  );
}

/* ─────────────────────────── piezas ─────────────────────────── */

/**
 * El encabezado de un grupo del índice.
 *
 * Existe para que los tres grupos no puedan divergir, y porque el estilo tiene
 * una razón que se pierde si se copia y pega: la línea va ARRIBA, no debajo del
 * texto. Debajo separaría el título de los ítems que encabeza —justo lo
 * contrario de lo que hace falta—; arriba separa este bloque del anterior y
 * deja el título pegado a lo suyo.
 *
 * Y el espacio es asimétrico a propósito: mucho encima, poco debajo. Es lo que
 * hace que se lea como el techo de lo que viene y no como un ítem más de la
 * lista, que era el problema.
 */
function TituloGrupo({ children }: { children: React.ReactNode }) {
  return (
    <p className="mt-1 border-t border-borde px-3 pt-4 pb-1 text-[11px] font-semibold tracking-wider text-tenue-2 uppercase">
      {children}
    </p>
  );
}

function Salto({ a, children }: { a: string; children: React.ReactNode }) {
  return (
    <a
      href={a}
      className="flex items-center gap-2 rounded-lg px-3 py-1.5 text-tenue transition-colors hover:bg-superficie-2 hover:text-tinta"
    >
      {children}
    </a>
  );
}

const COLOR_METODO: Record<string, string> = {
  GET: "bg-dato-suave text-dato-tinta",
  POST: "bg-marca-suave text-marca-tinta",
  PUT: "bg-marca-suave text-marca-tinta",
  DELETE: "bg-peligro-suave text-peligro-tinta",
};

function Metodo({ metodo }: { metodo: string }) {
  return (
    <span
      className={cx(
        "cifra shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold tracking-wide",
        COLOR_METODO[metodo] ?? "bg-superficie-2 text-tenue",
      )}
    >
      {metodo}
    </span>
  );
}

/**
 * El SDK oficial (SPEC-027).
 *
 * Va DESPUÉS de "para tener en cuenta" y ANTES de las rutas, y el sitio importa:
 * quien llega ya entendió qué es esto y qué tiene que aguantar su sistema, que
 * es justo el momento en que enterarse de que existe una librería le ahorra el
 * trabajo. Ponerlo al final sería enseñárselo cuando ya escribió el cliente.
 *
 * Ni una frase de esta sección se escribe aquí: todo sale de `x-sdk`. Y los
 * ejemplos son el contenido literal de archivos que una prueba EJECUTA, así que
 * lo que se lee es código que funciona, no una ilustración.
 */
function ElCliente({ sdk }: { sdk: Sdk }) {
  return (
    <Seccion id="sdk" titulo={sdk.titulo}>
      {/* Los lenguajes junto al título: es lo primero que alguien quiere saber,
          y saberlo antes de leer el párrafo decide si sigue leyendo. */}
      {sdk.subtitulo && (
        <p className="-mt-2 text-sm font-medium text-marca">{sdk.subtitulo}</p>
      )}
      <Prosa>{sdk.entrada}</Prosa>

      {/* La garantía, destacada: es la única razón por la que vale la pena
          usarlo en vez de escribir treinta líneas de fetch. */}
      <div className="border-l-2 border-marca bg-marca-suave/40 py-3 pl-4">
        <p className="text-[11px] font-semibold tracking-wider text-tenue-2 uppercase">La garantía</p>
        <p className="mt-1 font-medium text-tinta">{sdk.garantia}</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        {sdk.paquetes.map((p) => (
          <div key={p.nombre} className="rounded-xl border border-borde p-4">
            <p className="text-[11px] font-semibold tracking-wider text-tenue-2 uppercase">
              {p.lenguaje}
            </p>
            <p className="cifra mt-1 text-sm text-tinta">{p.nombre}</p>
            <Codigo className="mt-3">{p.instalar}</Codigo>
            <p className="mt-3 text-sm text-tenue">{p.requiere}</p>
            <p className="mt-1 text-sm text-tenue">
              Licencia {p.licencia}
              {p.codigo && (
                <>
                  {" · "}
                  <a className="underline hover:text-marca" href={p.codigo} target="_blank" rel="noreferrer">
                    código
                  </a>
                </>
              )}
            </p>
          </div>
        ))}
      </div>

      <div>
        <h3 className="font-medium text-tinta">Lo único que escribes tú</h3>
        <ul className="mt-3 flex flex-col gap-4">
          {sdk.puertos.map((puerto) => (
            <li key={puerto.nombre} className="border-l-2 border-borde-fuerte pl-4">
              <p className="font-medium text-tinta">{puerto.nombre}</p>
              <p className="mt-1 text-sm text-tenue">{puerto.que}</p>
              {/* El porqué va con la firma, no aparte: una firma sin motivo se
                  implementa mal, y aquí implementarla mal significa guardar el
                  cursor fuera de la transacción del asiento. */}
              <p className="mt-2 text-sm text-tenue">
                <Prosa>{puerto.porque}</Prosa>
              </p>
            </li>
          ))}
        </ul>
      </div>

      <div>
        <h3 className="font-medium text-tinta">Lo que no hace</h3>
        <ul className="mt-2 flex list-disc flex-col gap-2 pl-5">
          {sdk.limites.map((l) => (
            <li key={l}>
              <Prosa>{l}</Prosa>
            </li>
          ))}
        </ul>
      </div>

      {sdk.ejemplos.map((e) => (
        <div key={e.archivo}>
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h3 className="font-medium text-tinta">{e.titulo}</h3>
            {/* De dónde sale, para que se vea que no es un fragmento inventado
                para la ocasión. */}
            <p className="cifra text-xs text-tenue-2">{e.archivo}</p>
          </div>
          <Codigo className="mt-2">{e.codigo}</Codigo>
        </div>
      ))}
    </Seccion>
  );
}

function Seccion({ id, titulo, children }: { id: string; titulo: string; children: React.ReactNode }) {
  return (
    <section id={id} className="scroll-mt-20 pt-12">
      <h2 className="text-2xl font-extrabold tracking-tight text-tinta">{titulo}</h2>
      <div className="mt-4 flex flex-col gap-4">{children}</div>
    </section>
  );
}

/**
 * La anatomía de un recurso, igual para todos: Url · Headers · Parameters ·
 * Body · Responses. Que sea siempre la misma es lo que permite leer el tercero
 * sin volver a aprender dónde mirar.
 */
function Recurso({ op, base }: { op: Operacion; base: string }) {
  const cabeceras = [
    ["Authorization", op.publica ? "No hace falta" : "Bearer <token>"],
    ...(op.exigeCliente ? [["X-Recauda-Cliente", "app"]] : []),
    ...(op.cuerpo ? [["Content-Type", "application/json"]] : []),
  ];

  return (
    <article id={anclaDe(op)} className="scroll-mt-20 rounded-2xl border border-borde bg-superficie p-5 sm:p-6">
      <header className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <h3 className="text-lg font-bold text-tinta">{op.resumen}</h3>
        {op.permiso ? (
          <span className="cifra rounded-full border border-borde bg-superficie-2 px-2 py-0.5 text-[11px] text-tenue">
            {op.permiso}
          </span>
        ) : (
          <span className="cifra rounded-full border border-exito/25 bg-exito-suave px-2 py-0.5 text-[11px] text-exito-tinta">
            público
          </span>
        )}
      </header>

      {/* En dos columnas cuando hay sitio: a la izquierda lo que se lee —qué hace
          y cuándo usarla—, a la derecha lo que se copia. Ensanchar la tarjeta sin
          repartirla solo mueve el hueco adentro: la descripción se acota a 64ch
          igual, y el resto queda estirado con aire a la derecha.

          `@container` y no `2xl:`: lo que decide si caben dos columnas es el
          ancho de la TARJETA, que cambia con el cajón lateral, no el de la
          ventana. */}
      <div className="@container">
        <div className="grid gap-x-8 @3xl:grid-cols-[minmax(0,24rem)_minmax(0,1fr)]">
          <div>
            {op.descripcion && (
              <div className="mt-3 max-w-[64ch]">
                <Prosa>{op.descripcion}</Prosa>
              </div>
            )}
          </div>

          <div className="min-w-0">
            <Rotulo primero>Url</Rotulo>
      <Codigo>
        {op.metodo} {base}
        {op.ruta}
      </Codigo>

      <Rotulo>Headers</Rotulo>
      <Tabla cabeceras={["Cabecera", "Valor"]} filas={cabeceras} compacta />

      {op.cuerpo && (
        <>
          <Rotulo>Body</Rotulo>
          {op.obligatorios.length > 0 && (
            <p className="mb-2 text-sm text-tenue">
              Obligatorios:{" "}
              {op.obligatorios.map((c, i) => (
                <span key={c}>
                  {i > 0 && ", "}
                  <code className="cifra text-tinta">{c}</code>
                </span>
              ))}
              .
            </p>
          )}
          <Codigo>{JSON.stringify(op.ejemplo ?? op.cuerpo, null, 2)}</Codigo>
        </>
      )}

      <Rotulo>Responses</Rotulo>
      <div className="flex flex-col gap-2">
        {op.respuestas.map(([codigo, texto]) => (
          <div key={codigo} className="flex gap-3 rounded-xl border border-borde bg-superficie-2/60 p-3">
            <span
              className={cx(
                "cifra shrink-0 text-sm font-semibold",
                codigo.startsWith("2") ? "text-exito" : codigo.startsWith("3") ? "text-dato" : "text-aviso-tinta",
              )}
            >
              {codigo}
            </span>
            <span className="text-sm text-tenue">
              <Prosa>{texto}</Prosa>
            </span>
          </div>
        ))}
      </div>
          </div>
        </div>
      </div>
    </article>
  );
}

const Rotulo = ({ children, primero }: { children: React.ReactNode; primero?: boolean }) => (
  <p className={cx("mb-2 text-[11px] font-semibold tracking-wider text-tenue-2 uppercase", primero ? "mt-3" : "mt-6")}>
    {children}
  </p>
);

const Codigo = ({ children, className }: { children: React.ReactNode; className?: string }) => (
  <pre
    className={cx(
      "cifra overflow-x-auto rounded-xl border border-borde bg-superficie-2 p-3.5 text-xs leading-relaxed whitespace-pre text-tinta",
      className,
    )}
  >
    {children}
  </pre>
);

function Tabla({
  cabeceras,
  filas,
  compacta,
}: {
  cabeceras: string[];
  filas: string[][];
  /** Dos columnas cortas —cabecera y valor— no quieren todo el ancho de la caja. */
  compacta?: boolean;
}) {
  return (
    <div
      className={cx(
        "overflow-x-auto rounded-xl border border-borde",
        compacta ? "w-fit min-w-[22rem]" : "max-w-4xl",
      )}
    >
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-superficie-2">
            {cabeceras.map((c) => (
              <th
                key={c}
                className="px-3.5 py-2 text-left text-[11px] font-semibold tracking-wider text-tenue-2 uppercase"
              >
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-borde">
          {filas.map(([a, b]) => (
            <tr key={a}>
              <td className={cx("px-3.5 py-2 align-top", compacta ? "cifra text-xs" : "cifra text-xs whitespace-nowrap")}>
                {a}
              </td>
              <td className="px-3.5 py-2 align-top text-tenue">
                {/* `?? ""`: las filas llegan del backend como string[][], así que
                    el tipo admite ausencia aunque en la práctica no ocurra. */}
                <Prosa>{b ?? ""}</Prosa>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Negrita y `código` del texto que viene del backend.
 *
 * Se parte y se reconstruye como nodos de React; nunca se inyecta HTML. El texto
 * lo escribimos nosotros, pero convertir esta página en una que pinta HTML de la
 * respuesta de una API es la clase de puerta que después alguien deja abierta.
 */
function Prosa({ children }: { children: string }) {
  const partes = children.split(/(\*\*[^*]+\*\*|`[^`]+`)/g).filter(Boolean);
  return (
    <>
      {partes.map((p, i) => {
        if (p.startsWith("**") && p.endsWith("**")) {
          // Recursivo: una negrita puede llevar `código` dentro, y sin esto
          // salían las comillas invertidas literales en pantalla.
          return (
            <strong key={i} className="font-semibold text-tinta">
              <Prosa>{p.slice(2, -2)}</Prosa>
            </strong>
          );
        }
        if (p.startsWith("`") && p.endsWith("`")) {
          return (
            <code key={i} className="cifra rounded bg-superficie-2 px-1 py-px text-[.92em] text-tinta">
              {p.slice(1, -1)}
            </code>
          );
        }
        return <span key={i}>{p}</span>;
      })}
    </>
  );
}
